// Voice Preset JSON schema + validator.
//
// Covers every input accepted by `gemini-3.1-flash-tts-preview`:
//   - single-speaker prebuilt voice  (`speechConfig.voiceConfig.prebuiltVoiceConfig`)
//   - multi-speaker configs          (`speechConfig.multiSpeakerVoiceConfig`)
//   - natural-language director prompt prefix (tone/pace/accent/energy/
//     reverb/emotion/pronunciation) — folded into the prompt text since the
//     TTS API has no dedicated fields for these; the model steers from the
//     preamble.
//
// When used with the Live model (`gemini-3.1-flash-live-preview`) only
// `base_voice` is honored. `speaker_configs` and director_notes-as-prompt-
// prefix do NOT apply because Live doesn't take a prompt prefix per turn and
// doesn't support multi-speaker configs. (Wired in the tts path, not Live.)
//
// IMPORTANT: this file is intentionally DUPLICATED between worker/ and
// frontend/ rather than symlinked — neither workspace imports across roots.
// Keep them byte-identical when changing the schema.

// ---- Keep in sync with frontend/src/constants/voices.ts VOICE_NAMES ----
export const VOICE_NAMES = [
  'Zephyr', 'Puck', 'Charon', 'Kore', 'Fenrir', 'Leda', 'Orus', 'Aoede',
  'Callirrhoe', 'Autonoe', 'Enceladus', 'Iapetus', 'Umbriel', 'Algieba',
  'Despina', 'Erinome', 'Algenib', 'Rasalgethi', 'Laomedeia', 'Achernar',
  'Alnilam', 'Schedar', 'Gacrux', 'Pulcherrima', 'Achird', 'Zubenelgenubi',
  'Vindemiatrix', 'Sadachbia', 'Sadaltager', 'Sulafat',
] as const;

export type VoiceName = (typeof VOICE_NAMES)[number];

export interface DirectorNotesPreset {
  tone?: string;
  pace?: string;
  accent?: string;
  energy?: string;
  reverb?: string;
  emotion?: string;
  pronunciation?: Record<string, string>;
}

export interface SpeakerConfigPreset {
  name: string;
  voice: string;
  director?: string;
}

export interface VoicePreset {
  name: string;
  description?: string;
  base_voice: string;
  director_notes?: DirectorNotesPreset;
  speaker_configs?: SpeakerConfigPreset[];
  sample_rate_hint?: number;
  version?: string;
}

export type ValidationResult =
  | { ok: true; preset: VoicePreset }
  | { ok: false; errors: string[] };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isStringNonEmpty(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

export function validateVoicePreset(input: unknown): ValidationResult {
  const errors: string[] = [];

  if (!isPlainObject(input)) {
    return { ok: false, errors: ['root must be a JSON object'] };
  }

  // name
  if (!isStringNonEmpty(input.name)) {
    errors.push('name: required non-empty string');
  }

  // description
  if (input.description !== undefined && typeof input.description !== 'string') {
    errors.push('description: must be a string if present');
  }

  // base_voice
  if (!isStringNonEmpty(input.base_voice)) {
    errors.push('base_voice: required non-empty string');
  } else if (!VOICE_NAMES.includes(input.base_voice as VoiceName)) {
    errors.push(
      `base_voice: "${input.base_voice}" is not a known prebuilt voice`,
    );
  }

  // director_notes
  let directorNotes: DirectorNotesPreset | undefined;
  if (input.director_notes !== undefined) {
    if (!isPlainObject(input.director_notes)) {
      errors.push('director_notes: must be an object if present');
    } else {
      const dn = input.director_notes;
      const fields: Array<keyof DirectorNotesPreset> = [
        'tone', 'pace', 'accent', 'energy', 'reverb', 'emotion',
      ];
      for (const f of fields) {
        if (dn[f] !== undefined && typeof dn[f] !== 'string') {
          errors.push(`director_notes.${f}: must be a string`);
        }
      }
      if (dn.pronunciation !== undefined) {
        if (!isPlainObject(dn.pronunciation)) {
          errors.push('director_notes.pronunciation: must be an object');
        } else {
          for (const [k, v] of Object.entries(dn.pronunciation)) {
            if (typeof v !== 'string') {
              errors.push(
                `director_notes.pronunciation.${k}: value must be a string`,
              );
            }
          }
        }
      }
      directorNotes = dn as DirectorNotesPreset;
    }
  }

  // speaker_configs
  let speakerConfigs: SpeakerConfigPreset[] | undefined;
  if (input.speaker_configs !== undefined) {
    if (!Array.isArray(input.speaker_configs)) {
      errors.push('speaker_configs: must be an array if present');
    } else {
      speakerConfigs = [];
      input.speaker_configs.forEach((entry, idx) => {
        if (!isPlainObject(entry)) {
          errors.push(`speaker_configs[${idx}]: must be an object`);
          return;
        }
        if (!isStringNonEmpty(entry.name)) {
          errors.push(`speaker_configs[${idx}].name: required non-empty string`);
        }
        if (!isStringNonEmpty(entry.voice)) {
          errors.push(`speaker_configs[${idx}].voice: required non-empty string`);
        } else if (!VOICE_NAMES.includes(entry.voice as VoiceName)) {
          errors.push(
            `speaker_configs[${idx}].voice: "${entry.voice}" is not a known prebuilt voice`,
          );
        }
        if (entry.director !== undefined && typeof entry.director !== 'string') {
          errors.push(`speaker_configs[${idx}].director: must be a string`);
        }
        speakerConfigs!.push({
          name: String(entry.name ?? ''),
          voice: String(entry.voice ?? ''),
          director: entry.director !== undefined ? String(entry.director) : undefined,
        });
      });
    }
  }

  // sample_rate_hint
  if (input.sample_rate_hint !== undefined) {
    if (
      typeof input.sample_rate_hint !== 'number' ||
      !Number.isFinite(input.sample_rate_hint) ||
      input.sample_rate_hint <= 0
    ) {
      errors.push('sample_rate_hint: must be a positive number');
    }
  }

  // version
  if (input.version !== undefined && typeof input.version !== 'string') {
    errors.push('version: must be a string if present');
  }

  if (errors.length > 0) return { ok: false, errors };

  const preset: VoicePreset = {
    name: String(input.name),
    base_voice: String(input.base_voice),
  };
  if (typeof input.description === 'string') preset.description = input.description;
  if (directorNotes) preset.director_notes = directorNotes;
  if (speakerConfigs) preset.speaker_configs = speakerConfigs;
  if (typeof input.sample_rate_hint === 'number') {
    preset.sample_rate_hint = input.sample_rate_hint;
  }
  if (typeof input.version === 'string') preset.version = input.version;

  return { ok: true, preset };
}

/**
 * Build a natural-language directive block to prepend to the TTS prompt
 * text. Returns empty string when notes and speakers both have no content.
 *
 * When `speakers` has two or more entries with per-speaker `director` hints,
 * a dedicated `Speakers:` roster is appended so the model can differentiate
 * deliveries. (Single-speaker or no-director rosters are skipped — the plain
 * `{speaker, voiceConfig}` list in the request already conveys the mapping.)
 */
export function buildDirectorPreamble(
  notes: DirectorNotesPreset | undefined,
  speakers?: SpeakerConfigPreset[] | undefined,
): string {
  const lines: string[] = [];
  if (notes) {
    if (notes.tone) lines.push(`Tone: ${notes.tone}`);
    if (notes.pace) lines.push(`Pace: ${notes.pace}`);
    if (notes.accent) lines.push(`Accent: ${notes.accent}`);
    if (notes.energy) lines.push(`Energy: ${notes.energy}`);
    if (notes.reverb) lines.push(`Reverb / acoustic space: ${notes.reverb}`);
    if (notes.emotion) lines.push(`Emotion: ${notes.emotion}`);
    if (notes.pronunciation && Object.keys(notes.pronunciation).length > 0) {
      const pairs = Object.entries(notes.pronunciation)
        .map(([k, v]) => `"${k}" -> "${v}"`)
        .join('; ');
      lines.push(`Pronunciation guide: ${pairs}`);
    }
  }

  const speakerLines: string[] = [];
  if (speakers && speakers.length >= 2) {
    for (const s of speakers) {
      if (!s.director) continue;
      speakerLines.push(`- ${s.name} (${s.voice}): ${s.director}`);
    }
  }

  if (lines.length === 0 && speakerLines.length === 0) return '';

  const out: string[] = [];
  if (lines.length > 0) {
    out.push('Voice performance direction (read in this style):');
    out.push(...lines.map((l) => `- ${l}`));
  }
  if (speakerLines.length > 0) {
    if (out.length > 0) out.push('');
    out.push('Speakers:');
    out.push(...speakerLines);
  }
  out.push('');
  return out.join('\n');
}

export function exampleVoicePreset(): VoicePreset {
  return {
    name: 'Warm Narrator (example)',
    description: 'Calm, measured documentary-style narration.',
    base_voice: 'Sulafat',
    director_notes: {
      tone: 'warm, intimate, reflective',
      pace: 'slow and deliberate, with brief pauses between clauses',
      accent: 'neutral transatlantic English',
      energy: 'low, grounded',
      reverb: 'small room, soft boundaries',
      emotion: 'thoughtful, unhurried',
      pronunciation: {
        SYNTH: 'sinth',
        Lyria: 'LEE-ree-ah',
      },
    },
    speaker_configs: [
      { name: 'Narrator', voice: 'Sulafat', director: 'primary voice' },
      { name: 'Guest', voice: 'Achird', director: 'warm, friendly interviewer' },
    ],
    sample_rate_hint: 24000,
    version: '1.0',
  };
}
